import { useState, useCallback, useRef, useEffect } from "react";
import { getAllOrderData, updateOrderStatus } from "../services/order-service.js";
import { getTranslation } from "../utils/translate.js";
import { useLanguage } from "../utils/languageContext.js";
import {
    BOARD_ACTIVE_STATUSES,
    BOARD_SCOPE,
    DELIVERED_UNDO_MS,
    formatLocalDate,
    mergeBoardOrders,
    todayDeliveryBounds
} from "../utils/orderBoard.js";

const POLL_INTERVAL_MS = 60000;

/**
 * In-memory session for the board. It survives navigating to an order and back,
 * and is gone on a browser reload — which is what clears the Delivered column.
 */
const boardSession = {
    chefId: null,
    key: null,
    board: null,
    deliveredOrders: new Map(),
    /** @type {Map<string, {original: Object, timeoutId: ReturnType<typeof setTimeout>, startedAt: number}>} */
    undoDelivering: new Map(),
    /** Latest mounted board hook; delayed DELIVERED commits go through here. */
    deliverCommitHandler: null
};

const clearUndoTimers = () => {
    for (const entry of boardSession.undoDelivering.values()) {
        clearTimeout(entry.timeoutId);
    }
    boardSession.undoDelivering.clear();
};

const snapshotUndoStartedAt = () => {
    const startedAt = {};
    for (const [id, entry] of boardSession.undoDelivering) {
        startedAt[id] = entry.startedAt;
    }
    return startedAt;
};

const mergeHoldIds = (pending) => new Set([...pending, ...boardSession.undoDelivering.keys()]);

/** Persist a delayed DELIVERED update if the board unmounted before the timer fired. */
const commitDeliveredFallback = async (id, original) => {
    try {
        await updateOrderStatus(original.id, "DELIVERED");
        boardSession.deliveredOrders.set(id, { ...original, status: "DELIVERED" });
    } catch (err) {
        console.error("Failed to move order on board:", err);
        boardSession.deliveredOrders.delete(id);
        if (boardSession.board?.orders?.[id]) {
            boardSession.board = {
                ...boardSession.board,
                orders: { ...boardSession.board.orders, [id]: original }
            };
        }
    }
};

const sessionKey = (chefId, scope) => `${chefId}|${scope}`;

const deliveredForScope = (scope) => {
    const today = formatLocalDate(new Date());
    const list = [...boardSession.deliveredOrders.values()];
    return scope === BOARD_SCOPE.TODAY
        ? list.filter((o) => String(o.deliveryDateTime).startsWith(today))
        : list;
};

const EMPTY_BOARD = { orders: {}, newIds: new Set() };

/**
 * Kanban board state: loads active orders once, then merges polls in place.
 * @param {Object} options
 * @param {string|number} options.chefId
 * @param {string} options.scope - BOARD_SCOPE.TODAY | BOARD_SCOPE.ALL
 * @param {Object} options.toastRef
 */
export const useOrderBoard = ({ chefId, scope, toastRef }) => {
    const { language } = useLanguage();

    if (chefId != null && boardSession.chefId !== chefId) {
        clearUndoTimers();
        boardSession.chefId = chefId;
        boardSession.key = null;
        boardSession.board = null;
        boardSession.deliveredOrders = new Map();
    }

    const key = sessionKey(chefId, scope);
    const cached = boardSession.key === key ? boardSession.board : null;

    const [board, setBoard] = useState(cached || EMPTY_BOARD);
    const [loading, setLoading] = useState(!cached);
    const [error, setError] = useState(null);
    const [pendingIds, setPendingIds] = useState(() => new Set());
    const [undoStartedAt, setUndoStartedAt] = useState(snapshotUndoStartedAt);

    const pendingRef = useRef(new Set());
    const boardRef = useRef(board);
    const seqRef = useRef(0);
    const loadedKeyRef = useRef(cached ? key : null);

    useEffect(() => {
        boardRef.current = board;
        if (loadedKeyRef.current === key) {
            boardSession.key = key;
            boardSession.board = board;
        }
    }, [board, key]);

    const fetchActive = useCallback(async () => {
        const bounds = scope === BOARD_SCOPE.TODAY ? todayDeliveryBounds() : {};
        const res = await getAllOrderData(chefId, {
            status: BOARD_ACTIVE_STATUSES.join(","),
            deliveryDateFrom: bounds.deliveryDateFrom,
            deliveryDateTo: bounds.deliveryDateTo,
            sortOrder: "asc"
        });
        return res?.orderDtoTreeSet || [];
    }, [chefId, scope]);

    const deliveredIds = () => new Set(boardSession.deliveredOrders.keys());

    /** Full load: replaces the board (keeping this session's delivered cards). Nothing is flagged new. */
    const loadBoard = useCallback(async () => {
        if (!chefId) return;
        const seq = ++seqRef.current;
        setLoading(true);
        setError(null);
        try {
            const fetched = await fetchActive();
            if (seq !== seqRef.current) return;
            const delivered = deliveredForScope(scope);
            const prev = { orders: Object.fromEntries(delivered.map((o) => [String(o.id), o])), newIds: new Set() };
            loadedKeyRef.current = key;
            setBoard(
                mergeBoardOrders(prev, fetched, {
                    pendingIds: mergeHoldIds(pendingRef.current),
                    deliveredIds: deliveredIds(),
                    markNew: false
                })
            );
        } catch (err) {
            console.error("Error loading order board:", err);
            if (seq !== seqRef.current) return;
            setError(err);
        } finally {
            if (seq === seqRef.current) setLoading(false);
        }
    }, [chefId, fetchActive, scope, key]);

    /** Background poll: merges into the current board, flags unseen orders as new. */
    const pollBoard = useCallback(async () => {
        if (!chefId || loadedKeyRef.current !== key) return;
        const seq = seqRef.current;
        try {
            const fetched = await fetchActive();
            if (seq !== seqRef.current) return;
            setBoard((prev) =>
                mergeBoardOrders(prev, fetched, {
                    pendingIds: mergeHoldIds(pendingRef.current),
                    deliveredIds: deliveredIds()
                })
            );
        } catch (err) {
            console.error("Error polling order board:", err);
        }
    }, [chefId, fetchActive, key]);

    useEffect(() => {
        if (!chefId) return;
        if (loadedKeyRef.current === key && boardSession.key === key) {
            // Returning to the board: show what we had and pick up anything that arrived meanwhile.
            pollBoard();
        } else {
            loadedKeyRef.current = null;
            setBoard(EMPTY_BOARD);
            loadBoard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chefId, key]);

    useEffect(() => {
        if (!chefId) return undefined;
        const onVisibility = () => {
            if (!document.hidden) pollBoard();
        };
        document.addEventListener("visibilitychange", onVisibility);
        const interval = setInterval(() => {
            if (!document.hidden) pollBoard();
        }, POLL_INTERVAL_MS);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [chefId, pollBoard]);

    const dismissNew = useCallback((orderId) => {
        const id = String(orderId);
        setBoard((prev) => {
            if (!prev.newIds.has(id)) return prev;
            const newIds = new Set(prev.newIds);
            newIds.delete(id);
            return { ...prev, newIds };
        });
    }, []);

    const setPending = (id, on) => {
        if (on) pendingRef.current.add(id);
        else pendingRef.current.delete(id);
        setPendingIds(new Set(pendingRef.current));
    };

    const syncUndoStartedAt = () => setUndoStartedAt(snapshotUndoStartedAt());

    const applyLocalStatus = (id, status) => {
        setBoard((prev) => {
            if (!prev.orders[id]) return prev;
            const newIds = new Set(prev.newIds);
            newIds.delete(id);
            return { orders: { ...prev.orders, [id]: { ...prev.orders[id], status } }, newIds };
        });
    };

    const cancelUndo = useCallback((id) => {
        const entry = boardSession.undoDelivering.get(id);
        if (!entry) return null;
        clearTimeout(entry.timeoutId);
        boardSession.undoDelivering.delete(id);
        boardSession.deliveredOrders.delete(id);
        syncUndoStartedAt();
        return entry;
    }, []);

    const commitDelivered = useCallback(
        async (id, original) => {
            setPending(id, true);
            syncUndoStartedAt();
            try {
                await updateOrderStatus(original.id, "DELIVERED");
                boardSession.deliveredOrders.set(id, { ...original, status: "DELIVERED" });
            } catch (err) {
                console.error("Failed to move order on board:", err);
                boardSession.deliveredOrders.delete(id);
                setBoard((prev) => (prev.orders[id] ? { ...prev, orders: { ...prev.orders, [id]: original } } : prev));
                toastRef?.current?.addToast(getTranslation("toast.updateFailed", language), "error");
            } finally {
                setPending(id, false);
            }
        },
        [language, toastRef]
    );

    useEffect(() => {
        boardSession.deliverCommitHandler = commitDelivered;
        return () => {
            if (boardSession.deliverCommitHandler === commitDelivered) {
                boardSession.deliverCommitHandler = null;
            }
        };
    }, [commitDelivered]);

    const persistStatus = useCallback(
        async (id, original, toStatus) => {
            setPending(id, true);
            try {
                await updateOrderStatus(original.id, toStatus);
                if (toStatus === "DELIVERED") {
                    boardSession.deliveredOrders.set(id, { ...original, status: toStatus });
                } else {
                    boardSession.deliveredOrders.delete(id);
                }
            } catch (err) {
                console.error("Failed to move order on board:", err);
                setBoard((prev) => (prev.orders[id] ? { ...prev, orders: { ...prev.orders, [id]: original } } : prev));
                toastRef?.current?.addToast(getTranslation("toast.updateFailed", language), "error");
            } finally {
                setPending(id, false);
            }
        },
        [language, toastRef]
    );

    const moveOrder = useCallback(
        async (orderId, toStatus) => {
            const id = String(orderId);
            const current = boardRef.current.orders[id];
            if (!current || current.status === toStatus || pendingRef.current.has(id)) return;

            const undoEntry = boardSession.undoDelivering.get(id);

            if (toStatus === "DELIVERED") {
                const original = undoEntry?.original ?? current;
                if (undoEntry) cancelUndo(id);

                const startedAt = Date.now();
                const timeoutId = setTimeout(() => {
                    const pendingUndo = boardSession.undoDelivering.get(id);
                    if (!pendingUndo) return;
                    boardSession.undoDelivering.delete(id);
                    const handler = boardSession.deliverCommitHandler;
                    if (handler) handler(id, pendingUndo.original);
                    else commitDeliveredFallback(id, pendingUndo.original);
                }, DELIVERED_UNDO_MS);

                boardSession.undoDelivering.set(id, { original, timeoutId, startedAt });
                boardSession.deliveredOrders.set(id, { ...original, status: "DELIVERED" });
                syncUndoStartedAt();
                applyLocalStatus(id, "DELIVERED");
                return;
            }

            if (undoEntry) {
                cancelUndo(id);
                applyLocalStatus(id, toStatus);
                if (toStatus === undoEntry.original.status) return;
                await persistStatus(id, undoEntry.original, toStatus);
                return;
            }

            applyLocalStatus(id, toStatus);
            await persistStatus(id, current, toStatus);
        },
        [persistStatus, cancelUndo]
    );

    const revertDelivered = useCallback(
        (orderId) => {
            const id = String(orderId);
            const entry = boardSession.undoDelivering.get(id);
            if (!entry) return;
            moveOrder(id, entry.original.status);
        },
        [moveOrder]
    );

    return {
        orders: board.orders,
        newIds: board.newIds,
        pendingIds,
        undoStartedAt,
        loading,
        error,
        reload: loadBoard,
        moveOrder,
        revertDelivered,
        dismissNew
    };
};
